import { ArrowForward } from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useRepCodes, useUser } from '../hooks'
import { repCodeType } from '../types'
import './NewRepCode.css'
import axios from 'axios'
import { fieldLengths } from '../fieldLengths'
import dayjs from 'dayjs'

export default function NewRepCode(props: {
  mode: 'add' | 'edit' | null
  editRepCode?: repCodeType
  onClose: () => void
}) {
  const user = useUser()
  const editor = user.admin || user.operations || user.email === 'ckubacki@imprestechnology.com'
  const [repCodes, setRepCodes] = useRepCodes()

  const [newRepCode, setNewRepCode] = useState<repCodeType>({
    RepCode: '',
    IsActive: true,
    AccountExecutive: '',
    AeEmail: '',
    InsideSalesRep: '',
    IsrEmail: '',
    Engineer: '',
    EngEmail: '',
    CreatedTimestamp: null,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (props.editRepCode) {
      setNewRepCode(structuredClone(props.editRepCode))
    }
  }, [props.editRepCode])

  const handleSubmit = () => {
    setLoading(true)
    setError(false)
    setSuccess(false)

    axios
      .post(props.mode === 'add' ? '/addRepCode' : `/editRepCode/${props.editRepCode?.RepCode}`, newRepCode)
      .then(() => {
        if (props.mode === 'add') {
          setRepCodes(prev => [...prev, { ...newRepCode, CreatedTimestamp: new Date() }])
        } else {
          setRepCodes(prev =>
            prev.map(repCode => (repCode.RepCode === props.editRepCode?.RepCode ? newRepCode : repCode)),
          )
        }
        handleClose()
        setSuccess(true)
      })
      .catch(err => {
        console.error(err)
        setError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleClose = () => {
    props.onClose()
    setNewRepCode({
      RepCode: '',
      IsActive: true,
      AccountExecutive: '',
      AeEmail: '',
      InsideSalesRep: '',
      IsrEmail: '',
      Engineer: '',
      EngEmail: '',
      CreatedTimestamp: null,
    })
    setError(false)
    setSuccess(false)
    setLoading(false)
  }

  return (
    <>
      <Drawer open={!!props.mode} onClose={handleClose} anchor="right">
        <div className="new-repcode">
          <div className="new-repcode-header">
            <Typography variant="h6">{props.mode === 'add' ? 'Add Rep Code' : 'Edit Rep Code'}</Typography>
            <IconButton onClick={handleClose}>
              <ArrowForward />
            </IconButton>
          </div>
          <Divider flexItem />
          <div className="new-repcode-body">
            <Grid container spacing="17px">
              <Grid size={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Rep Code"
                  variant="outlined"
                  value={newRepCode.RepCode}
                  disabled={props.mode === 'edit'}
                  onChange={e => setNewRepCode(prev => ({ ...prev, RepCode: e.target.value }))}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: fieldLengths.repCodes.repCode,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  select
                  disabled={!editor}
                  size="small"
                  label="Active"
                  variant="outlined"
                  value={newRepCode.IsActive}
                  onChange={e => setNewRepCode(prev => ({ ...prev, IsActive: e.target.value === 'true' }))}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  autoSelect
                  disabled={!editor}
                  size="small"
                  options={Array.from(new Set(repCodes.map(repCode => repCode.Engineer))).filter(Boolean)}
                  getOptionLabel={option => option ?? ''}
                  renderInput={params => <TextField {...params} label="Engineer" />}
                  value={newRepCode.Engineer}
                  onChange={(_, value) =>
                    setNewRepCode(prev => ({
                      ...prev,
                      Engineer: value ?? '',
                      EngEmail: value
                        ? repCodes.find(repCode => repCode.Engineer === value)?.EngEmail ?? prev.EngEmail
                        : prev.EngEmail,
                    }))
                  }
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  disabled={!editor}
                  size="small"
                  label="Eng Email"
                  variant="outlined"
                  value={newRepCode.EngEmail}
                  onChange={e => setNewRepCode(prev => ({ ...prev, EngEmail: e.target.value }))}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: fieldLengths.repCodes.engEmail,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  autoSelect
                  disabled={!editor}
                  size="small"
                  options={Array.from(new Set(repCodes.map(repCode => repCode.AccountExecutive))).filter(Boolean)}
                  getOptionLabel={option => option ?? ''}
                  renderInput={params => <TextField {...params} label="Account Executive" />}
                  value={newRepCode.AccountExecutive}
                  onChange={(_, value) =>
                    setNewRepCode(prev => ({
                      ...prev,
                      AccountExecutive: value ?? '',
                      AeEmail: value
                        ? repCodes.find(repCode => repCode.AccountExecutive === value)?.AeEmail ?? prev.AeEmail
                        : prev.AeEmail,
                    }))
                  }
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  disabled={!editor}
                  size="small"
                  label="AE Email"
                  variant="outlined"
                  value={newRepCode.AeEmail}
                  onChange={e => setNewRepCode(prev => ({ ...prev, AeEmail: e.target.value }))}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: fieldLengths.repCodes.aeEmail,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={6}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  autoSelect
                  disabled={!editor}
                  size="small"
                  options={Array.from(new Set(repCodes.map(repCode => repCode.InsideSalesRep))).filter(Boolean)}
                  getOptionLabel={option => option}
                  renderInput={params => <TextField {...params} label="Inside Sales Rep" />}
                  value={newRepCode.InsideSalesRep}
                  onChange={(_, value) =>
                    setNewRepCode(prev => ({
                      ...prev,
                      InsideSalesRep: value ?? '',
                      IsrEmail: value
                        ? repCodes.find(repCode => repCode.InsideSalesRep === value)?.IsrEmail ?? prev.IsrEmail
                        : prev.IsrEmail,
                    }))
                  }
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  disabled={!editor}
                  size="small"
                  label="ISR Email"
                  variant="outlined"
                  value={newRepCode.IsrEmail}
                  onChange={e => setNewRepCode(prev => ({ ...prev, IsrEmail: e.target.value }))}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: fieldLengths.repCodes.isrEmail,
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
            <div className="new-repcode-actions">
              {props.mode === 'edit' && newRepCode.CreatedTimestamp && (
                <Typography variant="caption" color="textSecondary">
                  Created {dayjs(newRepCode.CreatedTimestamp).format('MM/DD/YYYY hh:mm A')}
                </Typography>
              )}
              {editor && (
                <Button
                  variant="contained"
                  size="large"
                  disabled={
                    !newRepCode.RepCode ||
                    !newRepCode.InsideSalesRep ||
                    !newRepCode.IsrEmail ||
                    (!!newRepCode.AccountExecutive && !newRepCode.AeEmail) ||
                    (!!newRepCode.Engineer && !newRepCode.EngEmail)
                  }
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Save {props.mode === 'add' ? 'and Add' : ''}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
      <Snackbar
        open={success}
        autoHideDuration={5000}
        onClose={() => setSuccess(false)}
        message={`Rep code ${props.mode === 'add' ? 'added' : 'edited'} successfully`}
      />
      <Snackbar open={error}>
        <Alert severity="error" variant="filled" onClose={() => setError(false)}>
          Failed to {props.mode === 'add' ? 'add' : 'edit'} rep code. Please try again or contact support if the problem
          persists.
        </Alert>
      </Snackbar>
    </>
  )
}
